/**
 * Centralne miejsce konfiguracji rezerwacji, płatności i danych organizacyjnych.
 *
 * ZASADA: wartość `null` oznacza dane, których jeszcze nie mamy — na stronie
 * pojawia się wtedy widoczne oznaczenie TODO ("[DO UZUPEŁNIENIA]"). Po wpisaniu
 * prawdziwej treści oznaczenie znika automatycznie.
 */
import kamilaPhoto from "@/assets/rekomendacja-mastermind.jpg.asset.json";

export const TODO = "[DO UZUPEŁNIENIA]";

/** Jednolita etykieta doświadczenia 1:1 — używana w całym serwisie. */
export const SESSIONS_LABEL = "10 000 h pracy 1:1";

/** Jednolita etykieta liczby godzin mastermindu — używana w całym serwisie. */
export const GROUP_HOURS_LABEL = "minimum 37 godzin";

/** Jednolity adres spotkań stacjonarnych (konsultacje i mastermind). */
export const STATIONARY_LOCATION = "Warszawa, pl. Konstytucji 6/74";

/**
 * BOOKING_URL — pełny adres systemu rezerwacji i płatności za pierwsze
 * konsultację indywidualną. Dopóki jest `null`, przyciski „Zarezerwuj pierwsze
 * spotkanie” kierują na stronę kontaktu.
 */
export const BOOKING_URL: string | null = null;

export type DetailItem = { label: string; value: string | null };

/**
 * Informacje praktyczne, konsultacje indywidualne.
 */
export const sessionDetails: DetailItem[] = [
  { label: "Forma", value: "Online lub osobiście" },
  { label: "Rytm dalszej pracy", value: "Zwykle 1-2 razy w miesiącu, psychoterapia co tydzień." },
  { label: "Poufność", value: "Pełna poufność i dyskrecja" },
  { label: "Czas trwania spotkania", value: "90 minut" },
  {
    label: "Lokalizacja spotkań stacjonarnych",
    value: STATIONARY_LOCATION,
  },
  {
    label: "Cena",
    value: "1 000 zł brutto (usługa zwolniona z VAT)",
  },
  {
    label: "Faktura",
    value: "Fakturę wystawiam do każdego spotkania. Wystarczy podać dane.",
  },
  // Zasady zmiany i odwołania terminu ustalamy w rozmowie — nie publikujemy ich tutaj.
];

/** Domyślny tekst dla ceny. */
export const ONE_PRICE_FALLBACK = "1 000 zł (brutto, zwolnione z VAT)";

/** Jednolity opis procesu rezerwacji konsultacji, używany na /konsultacje i /rezerwacja. */
export const ONE_RESERVATION_FLOW =
  "Wybierasz termin, potwierdzam go i wysyłam dane do płatności."; 

/**
 * GROUP_CALL_URL — pełny adres systemu rezerwacji bezpłatnej 30-minutowej
 * rozmowy wstępnej do mastermindu. Bez adresu przyciski kierują na kontakt.
 */
export const GROUP_CALL_URL: string | null = null;

/** Status najbliższej edycji pokazywany w hero mastermindu. */
export const groupStatus: { label: string; value: string | null }[] = [
  // Po ustaleniu terminu wpisz np. "Październik 2026"
  { label: "Najbliższa edycja", value: "Termin w przygotowaniu" },
  // "Otwarty" / "Lista zainteresowanych" / "Zamknięty"
  { label: "Nabór", value: "Lista zainteresowanych, zostaw zgłoszenie, aby otrzymać informację jako pierwsza/pierwszy" },
];

/**
 * Organizacja cyklu mastermindu.
 */
export const groupProgram: DetailItem[] = [
  { label: "Długość cyklu", value: "Dziewięć miesięcy" },
  { label: "Liczba spotkań", value: `Dziewięć spotkań, ${GROUP_HOURS_LABEL}` },
  { label: "Częstotliwość", value: "Około raz w miesiącu" },
  {
    label: "Długość spotkania",
    value: "Dwa spotkania całodniowe stacjonarnie w Warszawie, pl. Konstytucji 6/74 i 7 spotkań online trwających od 3 do 4 godzin",
  },
  { label: "Liczba uczestników", value: "Maksymalnie 10 osób, stały skład" },
  { label: "Forma", value: "Spotkania stacjonarne i online" },
  { label: "Lokalizacja spotkań stacjonarnych", value: STATIONARY_LOCATION },
  { label: "Cena", value: "18 000 zł brutto (usługa zwolniona z VAT)" },
  {
    label: "Co obejmuje cena",
    value: `Cena 18 000 zł obejmuje udział w całym, 9-miesięcznym cyklu mastermindu: 9 spotkań i ${GROUP_HOURS_LABEL}, 2 całodniowe spotkania stacjonarne w Warszawie, pl. Konstytucji 6/74, 7 spotkań online trwających od 3 do 4 godzin, pracę w stałej, zamkniętej grupie liczącej maksymalnie 10 osób, przygotowanie i prowadzenie całego programu przez Sylwię Sitkowską. Cena dotyczy całego programu. Płatność następuje po rozmowie kwalifikacyjnej i potwierdzeniu udziału w grupie.`,
  },
];

/** Domyślny tekst ceny cyklu. */
export const GROUP_PRICE_FALLBACK = "18 000 zł (brutto, usługa zwolniona z VAT)";

/**
 * Poufność grupy.
 */
export const groupSafety: DetailItem[] = [
  {
    label: "Zasady poufności",
    value: "To, co dzieje się w grupie, zostaje w grupie. Obowiązuje pełna poufność.",
  },
];


/**
 * Kryteria wykluczające („Ta grupa nie będzie właściwa, jeśli…”).
 * Pusta tablica = sekcja ukryta.
 */
export const groupNotFor: string[] = [];

/**
 * Rekomendacje uczestników — pusta tablica ukrywa całą sekcję.
 */
export type GroupTestimonial = {
  quote: string;
  author: string;
  role?: string;
  photo?: string;
  /** Dalsza część rekomendacji pokazywana po rozwinięciu „Czytaj więcej”. */
  rest?: string[];
};

export const groupTestimonials: GroupTestimonial[] = [
  {
    photo: kamilaPhoto.url,
    author: "Kamila Szalach",
    role: "właścicielka Wyspa Dzieci, Femmeritum",
    quote:
      "Sylwia stworzyła naszą grupę mastermindową, ale mam poczucie, że stworzyła coś znacznie większego niż po prostu przestrzeń do rozmów o biznesie.",
    rest: [
      "Jej sposób prowadzenia sprawia, że ludzie bardzo szybko zaczynają rozmawiać ze sobą inaczej, głębiej, szczerzej, bez masek i bez potrzeby udowadniania czegokolwiek. Od początku miała swoją wizję tej grupy, ale jednocześnie dała nam przestrzeń, żebyśmy wspólnie stworzyli jej zasady, kodeks i wzięli odpowiedzialność za to, jak chcemy ze sobą być.",
      "Po kilku miesiącach widzę bardzo wyraźnie, że ta grupa realnie wpływa na życie każdego z nas. I chyba właśnie to jest dla mnie w niej najważniejsze, nie chodzi tylko o to, że stajemy się lepszymi przedsiębiorcami. Mam poczucie, że stajemy się po prostu lepszymi ludźmi.",
      "Dajemy sobie bardzo dużo. Czasem wiedzę, czasem perspektywę, czasem wsparcie, a czasem po prostu prawdę, której sami nie chcielibyśmy sobie powiedzieć. I równie dużo od siebie bierzemy.",
      "Najchętniej napisałabym tu o konkretnych sytuacjach, bo to one najlepiej pokazałyby wartość tej grupy. Ale jednym z naszych najważniejszych kontraktów jest to, że wszystko, co dzieje się między nami, zostaje między nami.",
      "Dlatego zamiast opowiadać, co dokładnie wydarzyło się u nas, mogę powiedzieć tylko jedno: jeśli masz możliwość wejść do grupy prowadzonej przez Sylwię, po prostu sprawdź to na własnej skórze.",
    ],
  },
];
